/*
 * Copyright (c) 2019. Nedaware SL
 */

import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {Resource} from '../../api/resource';
import {Type} from '@angular/core';
import {ComponentType} from "@angular/cdk/portal";
import {BaseDialog} from "../../basic-entity-front/basic-entity-table/base-dialog";
import {Caster} from "../casters/caster";
import {FORMATS} from "../../basic-entity-front/const";
import {MomentCaster} from "../casters/moment-caster";
import {JsonCaster} from "../casters/json-caster";
import {ErrorCaster} from "../casters/error-caster";
import {FinancialCaster} from "../casters/financial-caster";
import {NestedBehavior, NestedModelType} from "./nested-model-type";
import {PropertyTypeInterface} from "./property-type-interface";
import {TypeStr} from "./type-str";
import {ObjectType} from "./object-type";

export enum DictionaryValueType {
    String
    // Add here more to extends the dictionary type capabilities
}

/** Some casters to use in the types, so we do not repeat their instantiation */
const dateCaster = new MomentCaster(FORMATS.date);
const dateTimeCaster = new MomentCaster(FORMATS.datetime);
const timeCaster = new MomentCaster(FORMATS.time);

/**
 * Exported constant with the available property types. Only the types
 * listed here should be used, and not custom implementations of
 * the PropertyTypeInterface (since the types need to be known
 * by the BasicEntityTable and some other components).
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export const PropertyType = {
    /**
     * This type can be used to receive any object, without the BasicEntityInterface casting it.
     * It accepts, optionally, an object model which will be used to parse the objects received
     * accordingly.
     * @constructor
     */
    Object: (model: Type<any> | null = null) => new ObjectType(model),
    /**
     * This type can be used to manage simple dictionaries of basic types. For example:
     * {'a': 'This is an A', 'b': 'This is a B'}
     * @constructor
     */
    Dictionary: (type: DictionaryValueType) => new DictionaryType(type),
    /**
     * This type can be used to manage simple dictionaries of translations. For example:
     * {'es': 'Español', 'en': 'Spanish'}
     * @constructor
     */
    Translation: () => new DictionaryType(DictionaryValueType.String, TypeStr.Translation),
    /**
     * This type represents a common string
     * @constructor
     */
    String: (minLength?: number, maxLength?: number) => new LengthyType(TypeStr.String, ['string'], minLength, maxLength),
    /**
     * This type represents a long text
     * @constructor
     */
    Text: (minLength?: number, maxLength?: number) => new LengthyType(TypeStr.Text, ['string'], minLength, maxLength),
    /**
     * This type represents an internet link
     * @constructor
     */
    ExternalLink: (pattern?: string, minLength?: number, maxLength?: number) => new ExternalLinkType(pattern, ['string'], minLength, maxLength),
    /**
     * This type represents an email
     * @constructor
     */
    Email: (pattern?: string, minLength?: number, maxLength?: number) => new EmailType(pattern, minLength, maxLength),
    /**
     * This type represents a phone number
     */
    PhoneNumber: (minLength?: number, maxLength?: number) => new LengthyType(TypeStr.PhoneNumber, ['string'], minLength, maxLength),
    /**
     * Use this type with properties of type Uri that can't be set at the moment (probably because
     * of circular dependencies) and will have to be replaced by a real Uri type before being used.
     * @param fileForReplacement - The file where this type should be replaced by an URI type
     * @constructor
     */
    FutureUri: (fileForReplacement: string) => new InvalidType(
        'This type should have been replaced by an URI type in the file ' + fileForReplacement + '. But it seems you forgot about it.'),
    /**
     * An URI/IRI which identifies a model in a unique way
     * @param model - The model the URI represents (or a superior class of all the accepted models)
     * @constructor
     */
    Uri: (model: Function) => new UriType(model),
    /**
     * A nested model instance
     * @param model - The nested model
     * @param mappedBy - The property by which the parent is identified in the children (the nested model), if it exists
     * @param behavior - Defines if it will be possible to create the elements from there, and if they will be pickable from the existent ones
     * @param excludedProperties - If the behavior includes creation, this are the properties to NOT send to edition, by default all of the properties will be taken except the IRI and the entity type.
     *                  The mappedBy property will never be editable.
     * @param creationDialog - If provided, the indicated dialog will be used instead of default edition dialog
     * @constructor
     */
    NestedModel: <T extends Resource>(model: Type<T>,
                                      mappedBy?: string,
                                      behavior: NestedBehavior = NestedBehavior.PickOnly,
                                      excludedProperties?: string[],
                                      creationDialog?: ComponentType<BaseDialog<T>>) =>
        new NestedModelType<T>(model, mappedBy, behavior, excludedProperties, creationDialog),
    /**
     * An integer, it accepts a minimum and a maximum values optionally
     * @param min - The minimum value to accept
     * @param max - The maximum value to accept
     * @constructor
     */
    Integer: (min = null, max = null) => new IntegerType(min, max),
    /**
     * A floating point type, it accepts a minimum and a maximum values optionally
     * @param min - The minimum value to accept
     * @param max - The maximum value to accept
     * @constructor
     */
    Float: (min = null, max = null) => new FloatType(min, max),
    /**
     * Financial type, it is just a float type which will be displayed with the coin symbol
     * @param decimals - Number of decimal values to show
     * @param min - The minimum value to accept
     * @param max - The maximum value to accept
     * @constructor
     */
    Financial: (decimals = 2, min = null, max = null) => new FloatType(min, max, TypeStr.Financial, new FinancialCaster(decimals)),
    /**
     * A choice between several strings
     * @param args - The strings to choose between
     * @constructor
     */
    Choice: (...args) => new ChoiceType(...args),
    /**
     * A boolean, a simple true or false value
     * @constructor
     */
    Boolean: () => new SimpleType(TypeStr.Boolean, ['boolean']),
    /**
     * A decimal, a number with a predefined number of digits. It
     * will be stored and managed as a string.
     * @param precision - Total number of digits
     * @param scale - Number of decimal (fractionary) digits
     * @constructor
     */
    Decimal: (precision: number, scale: number) => new DecimalType(precision, scale),
    /**
     * A date and a time, a timestamp.
     * Managed internally as a Moment from Moment.js
     * @constructor
     */
    DateTime: () => new SimpleType(TypeStr.DateTime, ['string'], dateTimeCaster),
    /**
     * A date, represented in memmory by a Moment from Moment.js
     * @constructor
     */
    Date: () => new SimpleType(TypeStr.Date, ['string'], dateCaster),
    /**
     * Hours, minuts and seconds; represented in memmory by a Moment from Moment.js
     * @constructor
     */
    Time: () => new SimpleType(TypeStr.Time, ['string'], timeCaster),
    /**
     * This type represents an image
     * @constructor
     */
    Image: (acceptedExtensions: string[] = null, maxMb: number = Infinity) => new ImageType(acceptedExtensions, maxMb),
};

/**
 * Basic type with no validators
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
class SimpleType extends PropertyTypeInterface {
    public constructor(private _typeStr: TypeStr, public readonly expectedApiTypes: string[] = [], public stringCaster: Caster<any, string> | null = null) {
        super();
    }

    get validators(): ValidatorFn[] | ValidatorFn | null {
        return null;
    }

    public toString(): TypeStr {
        return this._typeStr;
    }
}

export interface TypeWithLengthInterface {
    /** Minimum length, as defined to use in an input[type=mail] */
    minLength?: number;
    /** Maximum length, as defined to use in an input[type=mail] */
    maxLength?: number;
}

class LengthyType extends SimpleType implements TypeWithLengthInterface {
    public constructor(typeStr: TypeStr,
                       readonly expectedApiTypes: string[] = [],
                       public readonly minLength?: number,
                       public readonly maxLength?: number) {
        super(typeStr);
    }

    asWithLength(): TypeWithLengthInterface {
        return this as TypeWithLengthInterface;
    }
}

export interface PatternTypeInterface {
    /** Pattern to apply, as defined to use in an input[type=mail] */
    pattern: string;
}

/**
 * The Email type
 */
class EmailType extends LengthyType implements PatternTypeInterface {
    public readonly pattern: string;

    public constructor(pattern?: string, minLength?: number, maxLength?: number) {
        super(TypeStr.Email, ['string'], minLength, maxLength);
        if (pattern) {
            this.pattern = pattern;
        } else {
            this.pattern = '(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,})$';
        }
    }

    asPattern(): PatternTypeInterface {
        return this as PatternTypeInterface;
    }
}

class ExternalLinkType extends LengthyType implements PatternTypeInterface {
    public readonly pattern: string;

    public constructor(pattern?: string, expectedApiTypes: string[] = [], minLength?: number, maxLength?: number) {
        super(TypeStr.ExternalLink, expectedApiTypes, minLength, maxLength);
        if (pattern) {
            this.pattern = pattern;
        } else {
            this.pattern = 'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)';
        }
    }

    asPattern(): PatternTypeInterface {
        return this as PatternTypeInterface;
    }
}

/**
 * Range types, they accept a minimum and maximum value.
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export interface RangeTypeInterface {
    min: number | null;
    max: number | null;
}

/**
 * Implementation for range types, validates the value checking it is
 * between min and max
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
abstract class RangeType extends PropertyTypeInterface implements RangeTypeInterface {
    public readonly expectedApiTypes: string[] = ['number'];

    public constructor(public min: number = null, public max: number = null) {
        super();
    }


    get validators(): ValidatorFn[] {
        const validators = [];
        if (this.min !== null) {
            validators.push(Validators.min(this.min));
        }
        if (this.max !== null) {
            validators.push(Validators.max(this.max));
        }
        return validators;
    }

    abstract toString(): TypeStr;

    public asRange(): RangeTypeInterface {
        return this as RangeTypeInterface;
    }
}

/**
 * This type validates that the value is an integer
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
class IntegerType extends RangeType {
    public toString(): TypeStr {
        return TypeStr.Integer;
    }

    get validators(): ValidatorFn[] {
        return [(c: AbstractControl): ValidationErrors | null => {
            if (c.value !== null && c.value !== Math.round(c.value)) {
                return {validInteger: true};
            }
            return null;
        }, ...super.validators];
    }
}

class FloatType extends RangeType {
    constructor(min: number = null, max: number = null, private _typeStr: TypeStr = TypeStr.Float, public stringCaster: Caster<number, string> = null) {
        super(min, max);
    }

    public toString(): TypeStr {
        return this._typeStr;
    }
}

/**
 * The decimal type accepts a precision and a scale
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export interface DecimalTypeInterface {
    precision: number;
    scale: number;
    readonly maxLength: number;
}

/**
 * Implementation of decimal type
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
class DecimalType extends PropertyTypeInterface implements DecimalTypeInterface {
    private readonly intlength: number;

    public constructor(
        public precision: number,
        public scale: number
    ) {
        super();
        if (this.precision <= 0) {
            throw Error(`${this.precision} is not a valid precision, should be greater than 0.`);
        }
        if (this.scale <= 0) {
            throw Error(`${this.scale} is not a valid scale, should be greater than 0.`);
        }
        if (this.scale > this.precision) {
            throw Error('Error creating decimal type: scale greater than precision.');
        }
        this.intlength = this.precision - this.scale;
    }

    public toString(): TypeStr {
        return TypeStr.Decimal;
    }

    asDecimal(): DecimalTypeInterface {
        return this as DecimalTypeInterface;
    }

    get maxLength(): number {
        return (this.intlength === 0 ? 1 : this.intlength) + this.scale + 2; // Dos extra para el punto y el signo
    }

    get validators(): ValidatorFn[] | ValidatorFn | null {
        return [
            (c: AbstractControl): ValidationErrors | null => {
                if (!/^([\-+]?[0-9]*[.,]?[0-9]*)?$/.test(c.value)) {
                    return {validatorDecimalValidFormat: {message: 'Formato de decimal inválido.'}};
                }
                return null;
            },
            (c: AbstractControl): ValidationErrors | null => {
                let val = c.value;
                if (!val) {
                    return null;
                }
                val = val.toString();
                const pointIdx = val.search(/[,.]|$/);
                if (pointIdx < 0) {
                    return null;
                }
                const integerPart = this.removeZeroesAndSign(val.substring(0, pointIdx), true);
                const decimalPart = this.removeZeroesAndSign(val.substr(pointIdx + 1), false);
                if (this.scale < decimalPart.length) {
                    return {
                        validatorDecimalScale: {
                            message: `El campo permite sólo ${this.scale > 1 ? this.scale + ' dígitos' : 'un dígito'} en la parte decimal.`
                        }
                    };
                }
                if (this.intlength < integerPart.length) {
                    return {
                        validatorDecimalPrecision: {
                            message: this.intlength === 0 ?
                                'El campo no permite dígitos no nulos en la parte entera' :
                                'El campo permite sólo ' +
                                `${this.intlength > 1 ? this.intlength + ' dígitos' : 'un dígito'} en la parte entera.`
                        }
                    };
                }
                return null;
            }
        ];
    }

    private removeZeroesAndSign(decimalStr: string, left: boolean): string {
        const exp = left ? /^[\-+]?0*(.*)$/ : /^([^0]*(0[^0]+)*)0*$/;
        const matchs = exp.exec(decimalStr);
        return matchs[1];
    }
}

/**
 * The choice type accepts only values within the choices array
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export interface ChoiceTypeInterface {
    choices: string[];
    /** Display label for a choice value; falls back to the raw value if no label was set for it */
    labelFor(choice: string): string;
}

/**
 * Implementation of ChoiceTypeInterface
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
class ChoiceType extends PropertyTypeInterface implements ChoiceTypeInterface {
    public choices: string[];

    public readonly expectedApiTypes: string[] = ['string'];
    public readonly validators = null;

    private _labels: Record<string, string> | null = null;

    public constructor(...choices: string[]) {
        super();
        this.choices = choices;

        if (this.choices.length === 0) {
            throw new Error('ChoiceType with no options to choose created!');
        }
    }

    public toString(): TypeStr {
        return TypeStr.Choice;
    }

    public asChoice(): ChoiceTypeInterface {
        return this as ChoiceTypeInterface;
    }

    /**
     * Sets display-only labels for the choices (the value sent to/received from the
     * API is never changed, only how it is printed in tables/dropdowns). Also wires
     * stringCaster so the table cell (which prints via toStrCaster, not labelFor) shows
     * the same labels.
     */
    public withLabels(labels: Record<string, string>): this {
        this._labels = labels;
        this.stringCaster = {
            toModel: (v: string | null) => v,
            fromModel: (v: string | null) => v == null ? null : this.labelFor(v),
        };
        return this;
    }

    public labelFor(choice: string): string {
        return this._labels?.[choice] ?? choice;
    }
}

/**
 * Uri types contain a reference to a model or some superior of a model
 * which allows us to obtain all the accepted interfaces from the interface
 * provider.
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export interface UriTypeInterface {
    readonly modelTypeOrParent: Function;
}

/**
 * Implementation of the UriTypeInterface
 *
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
class UriType extends SimpleType implements UriTypeInterface {
    public constructor(public readonly modelTypeOrParent: Function) {
        super(TypeStr.Uri, ['string', 'object'], new ErrorCaster('StringCaster for NestedModel should be replaced in the interface generation'));

    }

    get validators(): ValidatorFn[] | ValidatorFn | null {
        return null;
    }

    public asUri(): UriTypeInterface {
        return this as UriTypeInterface;
    }
}

class InvalidType extends PropertyTypeInterface {
    constructor(public message: string = 'Invalid type applied') {
        super();
    }

    public get validators(): ValidatorFn[] | ValidatorFn | null {
        throw new Error(this.message);
    }

    toString(): TypeStr {
        throw new Error(this.message);
    }
}

export interface DictionaryTypeInterface {
    readonly type: DictionaryValueType;
}

class DictionaryType extends SimpleType implements DictionaryTypeInterface {
    public readonly expectedApiTypes: string[] = ['object'];

    constructor(public readonly type: DictionaryValueType, typeStr: TypeStr = TypeStr.Dictionary) {
        super(typeStr);
        this.stringCaster = new JsonCaster();
    }

    asDictionary(): DictionaryTypeInterface {
        return this as DictionaryTypeInterface;
    }
}

class ImageType extends SimpleType {
    public readonly expectedApiTypes: string[] = ['string'];

    constructor(public readonly admittedExtensions: string[] | null = null, public readonly maxMb: number) {
        super(TypeStr.Image);
    }
}
